import { faCheck, faCircleCheck, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import useTimer from '../../hooks/useTimer';
import * as AppPropTypes from '../../lib/PropTypes';

import { useDisplay } from '../../contexts/LiveDisplayContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import CheckIcon from '../icons/Check';
import TimerPill from '../partials/TimerPill';

import styles from '../../../styles/displays/screens-layout.module.scss';

const propTypes = {
    step: PropTypes.shape({}).isRequired,
    game: AppPropTypes.game.isRequired,
    question: PropTypes.shape({}),
    leaderboard: PropTypes.shape({}).isRequired,
    onTimerEnd: PropTypes.func,
    children: PropTypes.node,
};

const defaultProps = {
    question: null,
    onTimerEnd: null,
    children: null,
};

function ScreensLayout({ step, game, question, onTimerEnd, children }) {
    const themeClassName = useThemeClassName(styles);
    const display = useDisplay();
    const { component } = step || {};
    const withHeader =
        component === 'question' ||
        component === 'leaderboard' ||
        component === 'end' ||
        component === 'games';

    const { leaderboard } = display || {};
    const { answers = null, participationsCount = null } = leaderboard || {};

    const { title = null, questions = null, step: stepId = null } = game || {};

    const stepParts = stepId !== null ? stepId.split('-') : null;
    const questionId = stepParts !== null && stepParts.length > 1 ? stepParts[1] : null;

    const answer = (answers || []).find(({ id: answerId }) => answerId === questionId) || null;
    const { total: answerParticipations = 0 } = answer || {};

    const { id = null, endAt = null } = question || {};
    const { ended, onEnded } = useTimer({ endAt, onEnd: onTimerEnd });
    const date = endAt !== null ? parseISO(endAt) : null;

    return (
        <div className={classNames([styles.container, themeClassName])}>
            {withHeader ? (
                <>
                    <div className={styles.header}>
                        <div className={styles.top} />
                        {question !== null ? (
                            <div className={styles.status}>
                                {participationsCount !== null && participationsCount > 0 ? (
                                    <div className={styles.count}>
                                        <div className={styles.item}>
                                            <FontAwesomeIcon
                                                className={styles.icon}
                                                icon={faUser}
                                            />
                                            <span className={styles.number}>
                                                {participationsCount}
                                            </span>
                                        </div>
                                        <div className={styles.item}>
                                            <FontAwesomeIcon
                                                className={styles.icon}
                                                icon={faCircleCheck}
                                            />
                                            <span className={styles.number}>
                                                {answerParticipations}
                                            </span>
                                        </div>
                                    </div>
                                ) : null}
                                <div className={styles.center}>
                                    <p className={styles.title}>{title}</p>
                                    <p className={styles.description}>
                                        <FormattedMessage
                                            defaultMessage="Question"
                                            description="Display label"
                                        />{' '}
                                        {id} / {(questions || []).length}
                                    </p>
                                </div>
                                {component === 'question' ? (
                                    <TimerPill
                                        className={styles.timer}
                                        date={date}
                                        started={!ended}
                                        onEnded={onEnded}
                                        showDigitsOnEnded
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <div className={styles.content}>{children}</div>
                </>
            ) : (
                children
            )}
        </div>
    );
}

ScreensLayout.propTypes = propTypes;
ScreensLayout.defaultProps = defaultProps;

export default ScreensLayout;
